import { ChevronRightIcon } from "@/components/atoms/icons/ChevronRightIcon";
import { TimerIconBold } from "@/components/atoms/icons/TimerIconBold";
import { TriangleAlertIcon } from "@/components/atoms/icons/TriangleAlertIcon";
import { Typography } from "@/components/atoms/Typography";
import { UI_PRIMITIVES } from "@/core/constants/uiColors";
import { useTranslation, useUIColors } from "@/core/hooks";
import { useI18nContext } from "@/core/providers/I18nProvider";
import React from "react";
import { Pressable, StyleSheet, View } from "react-native";

export interface PaymentSummaryCardProps {
  title: string;
  subtitle: string;
  missedCount: number;
  dueCount: number;
  totalOutstanding?: number; // Total outstanding amount
  onPress?: () => void;
  children?: React.ReactNode;
}

function PaymentSummaryCard({
  title,
  subtitle,
  missedCount,
  dueCount,
  totalOutstanding,
  onPress,
  children,
}: PaymentSummaryCardProps) {
  const colors = useUIColors();
  const { t } = useTranslation();
  const { isRTL } = useI18nContext();

  return (
    <Pressable
      accessibilityRole="button"
      onPress={onPress}
      style={({ pressed }) => [
        styles.card,
        {
          backgroundColor: colors.backgroundLevel1,
          borderWidth: 0,
          opacity: pressed ? 0.7 : 1,
        },
      ]}
    >
      {/* Header section: title + subtitle aligned per locale, with chevron */}
      <View
        style={[
          styles.headerContainer,
          isRTL
            ? { justifyContent: "flex-end" }
            : { justifyContent: "flex-start" },
        ]}
      >
        <View style={styles.singleRow}>
          <Typography
            variant="headline"
            color="primary"
            style={[styles.title, isRTL && styles.titleRtl]}
          >
            {title}
          </Typography>
          <View
            style={[
              styles.chevronContainer,
              isRTL ? { transform: [{ scaleX: -1 }] } : undefined,
            ]}
          >
            <ChevronRightIcon size={20} color={colors.fontPrimary} />
          </View>
        </View>
        <Typography variant="callout" color="secondary" style={styles.subtitle}>
          {subtitle}
        </Typography>
      </View>

      {/* Stats row: missed & due soon (and any extra children below) */}
      <View style={[styles.statsRow, isRTL && styles.statsRowRtl]}>
        <View style={[styles.statItem, isRTL && styles.statItemRtl]}>
          <View style={[styles.statHeader, { direction: "ltr" }]}>
            {isRTL ? (
              <>
                <Typography
                  variant="title3"
                  color="primary"
                  style={styles.statNumber}
                >
                  {missedCount}
                </Typography>
                <TriangleAlertIcon size={18} color={colors.fontErrorText} />
              </>
            ) : (
              <>
                <TriangleAlertIcon size={18} color={colors.fontErrorText} />
                <Typography
                  variant="title3"
                  color="primary"
                  style={styles.statNumber}
                >
                  {missedCount}
                </Typography>
              </>
            )}
          </View>
          <Typography
            variant="caption"
            color="tertiary"
            style={[styles.statLabel, isRTL && styles.statLabelRight]}
          >
            {t("payments.missedPayments")}
          </Typography>
        </View>

        <View style={[styles.statItem, isRTL && styles.statItemRtl]}>
          <View style={[styles.statHeader, { direction: "ltr" }]}>
            {isRTL ? (
              <>
                <Typography
                  variant="title3"
                  color="primary"
                  style={styles.statNumber}
                >
                  {dueCount}
                </Typography>
                <TimerIconBold size={18} color={UI_PRIMITIVES.yellowDark} />
              </>
            ) : (
              <>
                <TimerIconBold size={18} color={UI_PRIMITIVES.yellowDark} />
                <Typography
                  variant="title3"
                  color="primary"
                  style={styles.statNumber}
                >
                  {dueCount}
                </Typography>
              </>
            )}
          </View>
          <Typography
            variant="caption"
            color="tertiary"
            style={[styles.statLabel, isRTL && styles.statLabelRight]}
          >
            {t("payments.dueIn30Days")}
          </Typography>
        </View>
      </View>

      {children}

      {/* No background icon per Figma */}
    </Pressable>
  );
}

const styles = StyleSheet.create({
  card: {
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 20,
    gap: 20,
    position: "relative",
  },
  headerContainer: {
    gap: 4,
    alignItems: "flex-start",
  },
  headerContainerRtl: {
    alignItems: "flex-end",
  },
  singleRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 4,
    flexWrap: "nowrap",
  },
  titleSection: {
    gap: 4,
    flex: 2,
    minWidth: 0,
    alignItems: "flex-start",
  },
  titleSectionRtl: {
    alignItems: "flex-start",
  },
  titleRtl: {
    textAlign: "right",
  },
  title: {
    marginBottom: 0,
  },
  subtitle: {
    marginBottom: 0,
  },
  subtitleRtl: {
    textAlign: "right",
  },
  chevronContainer: {
    flexShrink: 0,
    alignSelf: "center",
  },
  statsRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-start",
    gap: 24,
  },
  statsRowRtl: {
    justifyContent: "flex-end",
  },
  statItem: {
    gap: 8,
    flex: 1,
    minWidth: 0,
    flexShrink: 1,
    alignItems: "flex-start",
  },
  statItemRtl: {
    alignItems: "flex-start",
  },
  statItemRight: {
    alignItems: "flex-end",
  },
  statLabelRight: {
    textAlign: "right",
  },
  statHeader: {
    flexDirection: "row",
    gap: 8,
    alignItems: "center",
  },
  statHeaderRtl: {
    flexDirection: "row-reverse",
  },
  statHeaderRight: {
    justifyContent: "flex-end",
  },
  statNumber: {
    marginBottom: 0,
    flexShrink: 1,
  },
  statLabel: {
    marginBottom: 0,
  },
  statLabelCenter: {
    textAlign: "center",
  },
  // backgroundIcon removed per Figma
});

// Export both named and default for compatibility
export { PaymentSummaryCard };

// Note: Removed React.memo to ensure translations update instantly when language changes
// React.memo can prevent re-renders when only context (like language) changes
// If performance becomes an issue, consider passing currentLanguage as a prop
export default PaymentSummaryCard;
